import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  useInboundRoutes, useSaveInboundRoute, useDeleteInboundRoute, useSchedules, useBusinessHours,
} from '../../lib/api/hooks-tms.js';
import { useSession } from '../../lib/auth/session.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, Segmented } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { Switch } from '../../components/ui/Form.js';
import { DestinationPicker, DestinationLabel } from '../../components/telephony/DestinationPicker.js';
import {
  HoursEditor, blankHours, hoursFromRules, rulesFromHours, invalidDay, type HoursDraft,
} from '../../components/telephony/HoursEditor.js';
import { RouteFlow } from './RouteFlow.js';
import type { Destination, InboundRoute } from '@arcnet/contracts';

/**
 * Inbound routes (docs/26 D1, D2).
 *
 * A route is named, numbers are assigned to it, and it says where their calls
 * go -- with an hours split on a schedule the account already has, or on hours
 * of its own. The list of routes sits beside the open one, and below the form
 * is what the route actually does, followed to the end of every branch.
 *
 * It was one route per number, edited on each number's row: the same decision
 * made again for every number that shared it.
 */

type HoursMode = InboundRoute['hours']['mode'];

/**
 * The time condition, in the order people think of it: none, the account's
 * business hours, or something custom. It was "Always the same / On a schedule /
 * Hours of its own", which read as three unrelated things (owner, 14 September
 * 2026). Only the wording and order moved: a route is still saved as hours mode
 * 'always', a 'schedule' (business hours being the account's nominated one), or
 * hours of its 'own', so no saved route changes.
 */
type HoursChoice = 'always' | 'business' | 'custom';
const CHOICES: Array<[HoursChoice, string]> = [
  ['always', 'Always open'], ['business', 'Business hours'], ['custom', 'Custom'],
];
const CUSTOM_KINDS: Array<['own' | 'schedule', string]> = [
  ['own', 'Hours for this route'], ['schedule', 'Another schedule'],
];

interface NumberChoice { id: string; e164: string; routeId: string | null; routeName: string | null }

export function InboundRoutesTab({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const writable = can('inboundroute:write');
  const routes = useInboundRoutes(tenantId);
  const [selected, setSelected] = useState<string | null>(null);
  const [seedNumber, setSeedNumber] = useState<string | null>(null);

  const list = useMemo(() => routes.data?.data ?? [], [routes.data]);
  const unrouted = useMemo(() => routes.data?.unrouted ?? [], [routes.data]);
  const current = selected && selected !== 'new' ? list.find((r) => r.id === selected) ?? null : null;

  // The first route opens by itself: an empty right-hand side is a wasted screen.
  useEffect(() => {
    if (selected === null && list[0]) setSelected(list[0].id);
  }, [list, selected]);

  /** Every number on the account, and the route it is on now. */
  const numbers: NumberChoice[] = useMemo(() => [
    ...list.flatMap((r) => r.numbers.map((n) => ({ id: n.id, e164: n.e164, routeId: r.id, routeName: r.name }))),
    ...unrouted.map((n) => ({ id: n.id, e164: n.e164, routeId: null, routeName: null })),
  ].sort((a, b) => a.e164.localeCompare(b.e164)), [list, unrouted]);

  const startNew = (numberId: string | null) => { setSeedNumber(numberId); setSelected('new'); };

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">Inbound routes</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            Named routes, the numbers on each, and where their calls go.
          </p>
        </div>
        <div className="arc-spacer" />
        {writable && (
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => startNew(null)}>
            <Icon name="plus" size={13} /> New route
          </button>
        )}
      </div>

      {routes.error && <ErrorBanner error={routes.error} />}

      {/* The one fault worth a banner: calls to these numbers are refused. */}
      {unrouted.length > 0 && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            <span className="arc-mono">{unrouted.map((n) => n.e164).join(', ')}</span>{' '}
            {unrouted.length === 1 ? 'is on no route, so calls to it are' : 'are on no route, so calls to them are'} refused.
          </span>
          {writable && (
            <button type="button" className="arc-btn arc-btn-sm" style={{ marginLeft: 'auto' }}
              onClick={() => startNew(unrouted[0]!.id)}>
              Route {unrouted.length === 1 ? 'it' : unrouted[0]!.e164}
            </button>
          )}
        </div>
      )}

      <div className="arc-routes">
        <nav className="arc-card arc-routes-list" aria-label="Inbound routes">
          {routes.isLoading ? <TableSkeleton rows={4} cols={1} /> : list.length === 0 ? (
            <Empty icon="ivr" title="No routes yet" hint="A route sends calls to your numbers somewhere." />
          ) : list.map((r) => (
            <button key={r.id} type="button" className="arc-route-item"
              aria-current={selected === r.id} onClick={() => { setSelected(r.id); setSeedNumber(null); }}>
              <span className="arc-row" style={{ gap: 6 }}>
                <strong className="arc-truncate">{r.name}</strong>
                <span className="arc-spacer" />
                {!r.is_enabled && <Badge tone="warn">off</Badge>}
                {r.is_enabled && r.hours.open_now !== null && (
                  <Badge tone={r.hours.open_now ? 'ok' : 'muted'}>{r.hours.open_now ? 'open' : 'closed'}</Badge>
                )}
              </span>
              <span className="arc-subtle arc-mono arc-truncate" style={{ fontSize: 11.5 }}>
                {r.numbers.length > 0 ? r.numbers.map((n) => n.e164).join(', ') : 'no numbers'}
              </span>
              <span className="arc-muted arc-truncate" style={{ fontSize: 12 }}>
                <DestinationLabel tenantId={tenantId} destination={r.destination} />
              </span>
            </button>
          ))}
        </nav>

        <div style={{ minWidth: 0 }}>
          {selected === 'new' || current ? (
            <RouteEditor
              key={current?.id ?? `new-${seedNumber ?? ''}`}
              tenantId={tenantId} existing={current} numbers={numbers}
              seedNumberId={seedNumber} writable={writable}
              onSaved={(id) => { setSelected(id); setSeedNumber(null); }}
              onDeleted={() => setSelected(null)}
              onCancel={() => { setSelected(list[0]?.id ?? null); setSeedNumber(null); }}
            />
          ) : !routes.isLoading && (
            <div className="arc-card">
              <Empty icon="ivr" title="Choose a route" hint="Its numbers, where calls go and what happens open here." />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/** One part of the route: a heading, what it decides, and the controls. */
function Part({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <section className="arc-method">
      <h3 className="arc-method-title">{title}</h3>
      <p className="arc-method-hint">{hint}</p>
      {children}
    </section>
  );
}

function RouteEditor({ tenantId, existing, numbers, seedNumberId, writable, onSaved, onDeleted, onCancel }: {
  tenantId: string;
  existing: InboundRoute | null;
  numbers: NumberChoice[];
  seedNumberId: string | null;
  writable: boolean;
  onSaved: (id: string) => void;
  onDeleted: () => void;
  onCancel: () => void;
}) {
  const save = useSaveInboundRoute(tenantId);
  const remove = useDeleteInboundRoute(tenantId);
  const schedules = useSchedules(tenantId);
  const business = useBusinessHours(tenantId);

  const [name, setName] = useState(existing?.name ?? '');
  const [numberIds, setNumberIds] = useState<string[]>(
    () => existing ? existing.numbers.map((n) => n.id) : seedNumberId ? [seedNumberId] : []);
  const [destination, setDestination] = useState<Destination | null>(existing?.destination ?? null);
  const [mode, setMode] = useState<HoursMode>(existing?.hours.mode ?? 'always');
  const [scheduleId, setScheduleId] = useState(
    existing?.hours.mode === 'schedule' ? existing.hours.schedule_id ?? '' : '');
  const [hours, setHours] = useState<HoursDraft>(
    () => existing?.hours.mode === 'own' ? hoursFromRules(existing.hours.rules) : blankHours());
  const [timezone, setTimezone] = useState(existing?.hours.mode === 'own' ? existing.hours.timezone : '');
  const [closed, setClosed] = useState<Destination | null>(existing?.closed_destination ?? null);
  const [prefix, setPrefix] = useState(existing?.cid_name_prefix ?? '');
  const [enabled, setEnabled] = useState(existing?.is_enabled ?? true);
  // Open or closed now is only known for the hours as saved.
  const [hoursEdited, setHoursEdited] = useState(false);
  /**
   * The time condition as picked. Null until somebody picks one and derived from
   * the saved hours until then, because which schedule is business hours arrives
   * in a request of its own.
   */
  const [picked, setPicked] = useState<HoursChoice | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const shared = schedules.data?.data ?? [];
  const chosen = shared.find((s) => s.id === scheduleId);
  const businessId = business.data?.schedule_id ?? null;
  const others = shared.filter((s) => s.id !== businessId);
  const choice: HoursChoice = picked
    ?? (mode === 'always' ? 'always'
      : mode === 'schedule' && scheduleId !== '' && scheduleId === businessId ? 'business'
        : 'custom');

  const pickHours = (c: HoursChoice) => {
    setPicked(c);
    setHoursEdited(true);
    if (c === 'always') {
      setMode('always');
    } else if (c === 'business') {
      setMode('schedule');
      setScheduleId(businessId ?? '');
    } else if (mode === 'always' || choice === 'business') {
      // Custom starts at hours for this route; another schedule is one click on.
      setMode('own');
    }
  };
  const failure = save.error ?? remove.error;

  const missing: string[] = [];
  if (!name.trim()) missing.push('a name');
  if (!destination) missing.push('where callers go');
  if (choice === 'business' && !businessId) missing.push('business hours for the account');
  if (choice === 'custom' && mode === 'schedule' && !scheduleId) missing.push('a schedule');
  if (mode !== 'always' && !closed) missing.push('where callers go outside the hours');
  if (mode === 'own' && invalidDay(hours)) missing.push('hours that close after they open');

  const savedOpenNow = !hoursEdited && existing && existing.hours.mode === mode
    && (mode !== 'schedule' || existing.hours.schedule_id === scheduleId)
    ? existing.hours.open_now : null;

  const toggleNumber = (id: string) =>
    setNumberIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const submit = () => save.mutate({
    ...(existing ? { id: existing.id } : {}),
    name: name.trim(),
    number_ids: numberIds,
    destination,
    hours: mode === 'always' ? { mode }
      // Business hours as they are when saved, even if they loaded after the pick.
      : mode === 'schedule' ? { mode, schedule_id: choice === 'business' ? businessId ?? scheduleId : scheduleId }
        : { mode, timezone: timezone.trim() || null, rules: rulesFromHours(hours) },
    closed_destination: mode === 'always' ? null : closed,
    cid_name_prefix: prefix.trim() || null,
    is_enabled: enabled,
  }, { onSuccess: (res) => onSaved(existing?.id ?? (res as { id: string }).id) });

  return (
    <div className="arc-card arc-card-body">
      <div className="arc-row" style={{ gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="arc-field" style={{ margin: 0, flex: '1 1 240px', maxWidth: 380 }}>
          <label className="arc-label" htmlFor="ir-name">{existing ? 'Route' : 'New route'}</label>
          <input id="ir-name" className="arc-input" maxLength={80} value={name} disabled={!writable}
            onChange={(e) => setName(e.target.value)} placeholder="Main line" />
        </div>
        {/* Up here beside the route's name rather than a section of its own at
            the bottom, where it was easy to miss (owner, 14 September 2026). */}
        <div className="arc-field" style={{ margin: 0, flex: '0 1 200px' }}>
          <label className="arc-label" htmlFor="ir-prefix">Prepend to caller ID</label>
          <input id="ir-prefix" className="arc-input" maxLength={40} value={prefix} disabled={!writable}
            onChange={(e) => setPrefix(e.target.value)} placeholder="Sales"
            title="Shown before the caller's name on the phones that ring, so whoever answers knows which number was called." />
        </div>
        <div className="arc-row" style={{ gap: 8, paddingBottom: 7 }}>
          <Switch id="ir-on" checked={enabled} disabled={!writable} onChange={setEnabled} />
          <label htmlFor="ir-on" style={{ cursor: 'pointer' }}>
            {enabled ? 'Taking calls' : 'Switched off: calls are refused'}
          </label>
        </div>
      </div>
      {failure != null && <ErrorBanner error={failure} />}

      <fieldset disabled={!writable} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        <Part title="Numbers"
          hint="Calls to these numbers follow this route. A number is on one route at a time, so choosing one on another route moves it here.">
          {numbers.length === 0 ? (
            <p className="arc-subtle" style={{ margin: 0 }}>No numbers are assigned to this account yet.</p>
          ) : (
            <div className="arc-route-numbers">
              {numbers.map((n) => {
                const on = numberIds.includes(n.id);
                const elsewhere = n.routeId !== null && n.routeId !== existing?.id;
                return (
                  <label key={n.id} className="arc-pick-row" style={{ cursor: 'pointer' }}>
                    <input type="checkbox" checked={on} onChange={() => toggleNumber(n.id)} />
                    <span className="arc-mono" style={{ fontWeight: 550 }}>{n.e164}</span>
                    <span className="arc-subtle arc-truncate" style={{ fontSize: 11.5 }}>
                      {elsewhere
                        ? (on ? `moves here from ${n.routeName}` : `on ${n.routeName}`)
                        : n.routeId === null && !on ? 'on no route' : ''}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </Part>

        <Part title="Callers go to" hint="Where a call to these numbers is sent.">
          <DestinationPicker tenantId={tenantId} value={destination} onChange={setDestination} />
        </Part>

        <Part title="Hours"
          hint="When calls go to the destination above, and where they go the rest of the time.">
          <Segmented label="Hours" value={choice} options={CHOICES} onChange={pickHours} />

          {choice === 'business' && (
            <p className="arc-hint" style={{ marginTop: 10 }}>
              {businessId
                ? `The account's business hours${business.data?.name ? ` (${business.data.name})` : ''}, set under Hours & schedules.`
                : 'This account has no business hours yet. Set them under Hours & schedules, or choose Custom.'}
            </p>
          )}

          {choice === 'custom' && (
            <div style={{ marginTop: 12 }}>
              <Segmented label="Custom hours" value={mode === 'schedule' ? 'schedule' : 'own'}
                options={CUSTOM_KINDS}
                onChange={(k) => {
                  setHoursEdited(true);
                  setMode(k);
                  if (k === 'schedule') setScheduleId((id) => (id === businessId ? '' : id));
                }} />

              {mode === 'schedule' && (
                <div className="arc-field" style={{ marginTop: 12, maxWidth: 380 }}>
                  <label className="arc-label" htmlFor="ir-schedule">Schedule</label>
                  <select id="ir-schedule" className="arc-select" value={scheduleId}
                    onChange={(e) => { setScheduleId(e.target.value); setHoursEdited(true); }}>
                    <option value="">Choose a schedule…</option>
                    {others.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  {others.length === 0 && (
                    <p className="arc-hint">No other schedules yet. Add one under Hours &amp; schedules, or set hours for this route.</p>
                  )}
                </div>
              )}

              {mode === 'own' && (
                <div style={{ marginTop: 12 }}>
                  <div className="arc-field" style={{ maxWidth: 260 }}>
                    <label className="arc-label" htmlFor="ir-tz">Timezone</label>
                    <input id="ir-tz" className="arc-input" value={timezone} placeholder="The account's"
                      onChange={(e) => { setTimezone(e.target.value); setHoursEdited(true); }} />
                  </div>
                  <HoursEditor idPrefix="ir-hours" value={hours}
                    onChange={(h) => { setHours(h); setHoursEdited(true); }} />
                </div>
              )}
            </div>
          )}

          {mode !== 'always' && (
            <div className="arc-field" style={{ marginTop: 12, marginBottom: 0 }}>
              <label className="arc-label">Outside those hours, callers go to</label>
              <DestinationPicker tenantId={tenantId} value={closed} onChange={setClosed} />
            </div>
          )}
        </Part>

      </fieldset>

      {writable && (
        <div className="arc-row" style={{ gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
          <button type="button" className="arc-btn arc-btn-primary"
            disabled={save.isPending || missing.length > 0} onClick={submit}>
            {save.isPending ? 'Saving…' : existing ? 'Save route' : 'Create route'}
          </button>
          <button type="button" className="arc-btn" onClick={onCancel}>Cancel</button>
          {missing.length > 0 && (
            <span className="arc-subtle" style={{ fontSize: 12 }}>Needs {missing.join(', ')}.</span>
          )}
          <div className="arc-spacer" />
          {existing && (confirmDelete ? (
            <>
              <span className="arc-muted" style={{ fontSize: 12.5 }}>
                {existing.numbers.length > 0
                  ? `${existing.numbers.map((n) => n.e164).join(', ')} will be on no route.`
                  : 'No numbers use it.'}
              </span>
              <button type="button" className="arc-btn arc-btn-danger arc-btn-sm" disabled={remove.isPending}
                onClick={() => remove.mutate(existing.id, { onSuccess: onDeleted })}>
                {remove.isPending ? 'Deleting…' : 'Delete route'}
              </button>
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                onClick={() => setConfirmDelete(false)}>Keep</button>
            </>
          ) : (
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => setConfirmDelete(true)}>
              <Icon name="trash" size={13} /> Delete
            </button>
          ))}
        </div>
      )}

      <Part title="What happens" hint="A flow diagram of the route as it stands in this form, to the end of every branch.">
        <RouteFlow tenantId={tenantId} title={name.trim() || undefined} route={{
          numbers: numbers.filter((n) => numberIds.includes(n.id)).map((n) => n.e164),
          destination,
          hours: mode === 'always' ? null : {
            label: choice === 'business'
              ? `Business hours${business.data?.name ? ` (${business.data.name})` : ''}`
              : mode === 'own'
                ? 'Custom hours'
                : chosen ? `Schedule: ${chosen.name}` : 'A schedule, not chosen yet',
            openNow: savedOpenNow ?? null,
          },
          closed: mode === 'always' ? null : closed,
          enabled,
        }} />
      </Part>
    </div>
  );
}
